import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Avatar,
  Typography,
  Paper,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Send,
  AttachFile,
  EmojiEmotions,
  MoreVert,
  ArrowBack,
  Search,
  Call,
  VideoCall,
  Info,
} from "@mui/icons-material";
import useChat from "../hooks/useChat";
import { firebaseAuth, storage, db } from "../../../context/Firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { useAuthState } from "react-firebase-hooks/auth";
import ChatBubble from "../../../shared/components/ChatBubble";
import { collection, getDocs, deleteDoc, doc as firestoreDoc, updateDoc, arrayUnion, arrayRemove, getDoc } from "firebase/firestore";

const ImprovedChat = ({
  selectedUserId,
  selectedUserName,
  selectedConversationId,
  onBack,
}) => {
  const [authUser] = useAuthState(firebaseAuth);
  const currentUserId = authUser?.uid;
  const currentUserName = authUser?.displayName || authUser?.email;

  const { messages = [], sendMessage, editMessage, deleteMessage, chatId, loadingChat, loadingOlder, hasMoreMessages, loadOlderMessages } = useChat(
    currentUserId,
    currentUserName,
    selectedUserId,
    selectedUserName,
    selectedConversationId
  );

  const [newMessage, setNewMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clearChatDialog, setClearChatDialog] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMessagesLenRef = useRef(0);

  // Auto-scroll to bottom only for new messages (not when loading older)
  useEffect(() => {
    if (messages.length > prevMessagesLenRef.current) {
      const addedAtEnd = messages.length - prevMessagesLenRef.current;
      // Only auto-scroll if a small number of messages were added (new message, not older batch)
      if (addedAtEnd <= 2 || prevMessagesLenRef.current === 0) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevMessagesLenRef.current = messages.length;
  }, [messages]);

  // Load older messages on scroll to top
  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container || loadingOlder || !hasMoreMessages) return;
    if (container.scrollTop < 100) {
      const prevHeight = container.scrollHeight;
      loadOlderMessages().then(() => {
        // Maintain scroll position after prepending older messages
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight - prevHeight;
        });
      });
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (newMessage.trim() === "" && !editingMessage) return;

    if (editingMessage) {
      await editMessage(editingMessage.id, newMessage);
      setEditingMessage(null);
    } else {
      await sendMessage(newMessage, "text");
    }
    setNewMessage("");
    setReplyingTo(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileRef = ref(storage, `chatImages/${uuidv4()}`);
      await uploadBytes(fileRef, file);
      const imageUrl = await getDownloadURL(fileRef);
      await sendMessage(imageUrl, "image", file);
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearChat = async () => {
    if (!chatId) return;
    
    try {
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      const snapshot = await getDocs(messagesRef);
      
      if (snapshot.empty) {
        alert("Nothing to clear");
        setClearChatDialog(false);
        handleMenuClose();
        return;
      }
      
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Update conversation's last message
      await updateDoc(firestoreDoc(db, 'chats', chatId), {
        lastMessage: '',
        lastMessageTimestamp: new Date(),
      });
      
      setClearChatDialog(false);
      handleMenuClose();
    } catch (error) {
      console.error("Error clearing chat:", error);
      alert("Failed to clear chat");
    }
  };

  const handleBlockUser = async () => {
    if (!currentUserId || !selectedUserId) return;
    
    try {
      const userRef = firestoreDoc(db, 'users', currentUserId);
      
      if (isBlocked) {
        // Unblock user
        await updateDoc(userRef, {
          blockedUsers: arrayRemove(selectedUserId)
        });
        setIsBlocked(false);
        alert(`${selectedUserName} has been unblocked`);
      } else {
        // Block user
        await updateDoc(userRef, {
          blockedUsers: arrayUnion(selectedUserId)
        });
        setIsBlocked(true);
        alert(`${selectedUserName} has been blocked`);
      }
      
      setBlockDialog(false);
      handleMenuClose();
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
      alert("Failed to update block status");
    }
  };

  // Check if user is blocked on mount
  useEffect(() => {
    const checkBlockStatus = async () => {
      if (!currentUserId || !selectedUserId) return;
      
      try {
        const userDoc = await getDoc(firestoreDoc(db, 'users', currentUserId));
        if (userDoc.exists()) {
          const blockedUsers = userDoc.data().blockedUsers || [];
          setIsBlocked(blockedUsers.includes(selectedUserId));
        }
      } catch (error) {
        console.error("Error checking block status:", error);
      }
    };
    
    checkBlockStatus();
  }, [currentUserId, selectedUserId]);

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    const messageText =
      typeof message.message === "string"
        ? message.message
        : message.message?.text || "";
    setNewMessage(messageText);
  };

  const handleDelete = async (messageId) => {
    await deleteMessage(messageId);
  };

  const filteredMessages = searchTerm
    ? messages.filter((msg) => {
        const text =
          typeof msg.message === "string"
            ? msg.message
            : msg.message?.text || "";
        return text.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : messages;

  if (!currentUserId) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Typography>Please login to chat</Typography>
      </Box>
    );
  }

  if (loadingChat) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--app-body-bg)",
      }}
    >
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: 2,
          bgcolor: "var(--app-accent)",
          color: "white",
          gap: 2,
        }}
      >
        {onBack && (
          <IconButton onClick={onBack} sx={{ color: "white" }}>
            <ArrowBack />
          </IconButton>
        )}

        <Avatar sx={{ bgcolor: "var(--app-accent-hover)" }}>
          {selectedUserName?.[0]?.toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {selectedUserName}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Online
          </Typography>
        </Box>

        <Tooltip title="Search">
          <IconButton
            sx={{ color: "white" }}
            onClick={() => {
              setSearchOpen(!searchOpen);
              if (searchOpen) {
                setSearchTerm(""); // Clear search term when closing
              }
            }}
          >
            <Search />
          </IconButton>
        </Tooltip>

        <Tooltip title="Voice Call">
          <IconButton sx={{ color: "white" }}>
            <Call />
          </IconButton>
        </Tooltip>

        <Tooltip title="Video Call">
          <IconButton sx={{ color: "white" }}>
            <VideoCall />
          </IconButton>
        </Tooltip>

        <IconButton sx={{ color: "white" }} onClick={handleMenuClick}>
          <MoreVert />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>
            <Info sx={{ mr: 1 }} /> Contact Info
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>Mute Notifications</MenuItem>
          <MenuItem onClick={() => { setClearChatDialog(true); handleMenuClose(); }}>Clear Chat</MenuItem>
          <MenuItem onClick={() => { setBlockDialog(true); handleMenuClose(); }}>{isBlocked ? 'Unblock User' : 'Block User'}</MenuItem>
        </Menu>

        {/* Clear Chat Confirmation Dialog */}
        <Dialog open={clearChatDialog} onClose={() => setClearChatDialog(false)}>
          <DialogTitle>Clear Chat</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to clear all messages in this chat? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearChatDialog(false)}>Cancel</Button>
            <Button onClick={handleClearChat} color="error" variant="contained">Clear Chat</Button>
          </DialogActions>
        </Dialog>

        {/* Block User Confirmation Dialog */}
        <Dialog open={blockDialog} onClose={() => setBlockDialog(false)}>
          <DialogTitle>{isBlocked ? 'Unblock User' : 'Block User'}</DialogTitle>
          <DialogContent>
            <Typography>
              {isBlocked 
                ? `Are you sure you want to unblock ${selectedUserName}? They will be able to send you messages again.`
                : `Are you sure you want to block ${selectedUserName}? You won't receive messages from them.`
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockDialog(false)}>Cancel</Button>
            <Button onClick={handleBlockUser} color={isBlocked ? "primary" : "error"} variant="contained">
              {isBlocked ? 'Unblock' : 'Block'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      {/* Search Bar */}
      {searchOpen && (
        <Paper sx={{ p: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      )}

      {/* Messages Area */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: 2,
          backgroundColor: "var(--bg-primary)",
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d9d9d9' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {loadingOlder && (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={20} />
          </Box>
        )}
        {filteredMessages.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Typography color="text.secondary">
              {searchTerm
                ? "No messages found"
                : "No messages yet. Start the conversation!"}
            </Typography>
          </Box>
        ) : (
          filteredMessages.map((msg, index) => {
            const isMyMessage = msg.senderId === currentUserId;
            const showAvatar =
              index === 0 ||
              filteredMessages[index - 1].senderId !== msg.senderId;

            return (
              <ChatBubble
                key={msg.id || msg.messageId || index}
                message={msg}
                isOwnMessage={isMyMessage}
                showAvatar={showAvatar}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReply={handleReply}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Reply Preview */}
      {replyingTo && (
        <Paper
          sx={{
            p: 1,
            mx: 2,
            bgcolor: "var(--bg-tertiary)",
            borderLeft: "4px solid var(--app-accent)",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="primary" fontWeight={600}>
                Replying to {replyingTo.senderName}
              </Typography>
              <Typography variant="body2" noWrap>
                {typeof replyingTo.message === "string"
                  ? replyingTo.message
                  : replyingTo.message?.text || ""}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setReplyingTo(null)}>
              ×
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Edit Preview */}
      {editingMessage && (
        <Paper
          sx={{
            p: 1,
            mx: 2,
            bgcolor: "var(--bg-tertiary)",
            borderLeft: "4px solid #ffc107",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" fontWeight={600}>
              Editing message
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setEditingMessage(null);
                setNewMessage("");
              }}
            >
              ×
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Input Area */}
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: 1.5,
          gap: 1,
          bgcolor: "var(--bg-secondary)",
        }}
      >
        <Tooltip title="Attach File">
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <AttachFile />
          </IconButton>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: "none" }}
        />

        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          placeholder="Type a message..."
          variant="outlined"
          size="small"
          sx={{
            bgcolor: "var(--input-bg)",
            borderRadius: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small">
                  <EmojiEmotions />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title={editingMessage ? "Update" : "Send"}>
          <span>
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={uploading || newMessage.trim() === ""}
              sx={{
                bgcolor: "var(--app-accent)",
                color: "white",
                "&:hover": { bgcolor: "var(--app-accent-hover)" },
                "&:disabled": { bgcolor: "#ccc" },
              }}
            >
              {uploading ? <CircularProgress size={24} /> : <Send />}
            </IconButton>
          </span>
        </Tooltip>
      </Paper>
    </Box>
  );
};

export default ImprovedChat;
